import { Injectable, computed, signal } from '@angular/core';
import type { IntegrationProfileResponse } from '@core/api/model/integrationProfileResponse';
import type { SelectOption } from '@app/types';

const STORAGE_KEY = '__OA_SELECTED_INTEGRATION_PROFILE_ID__';

/**
 * Global state for the currently selected integration profile.
 * Populated on app startup by the topbar profile selector.
 * Selected profile id is persisted in localStorage across page reloads.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationProfileStateService {
  private readonly profilesSignal = signal<IntegrationProfileResponse[]>([]);
  private readonly selectedProfileIdSignal = signal<string | null>(this.readStoredProfileId());
  private readonly profilesLoadingSignal = signal<boolean>(false);

  readonly profiles = this.profilesSignal.asReadonly();
  readonly selectedProfileId = this.selectedProfileIdSignal.asReadonly();
  readonly profilesLoading = this.profilesLoadingSignal.asReadonly();

  readonly profileOptions = computed<SelectOption[]>(() =>
    this.profilesSignal().map((p) => ({
      value: p.integrationProfileId,
      label: p.integrationProfileName,
    })),
  );

  readonly selectedProfile = computed<IntegrationProfileResponse | null>(() => {
    const id = this.selectedProfileIdSignal();
    if (id == null) return null;
    return this.profilesSignal().find((p) => p.integrationProfileId === id) ?? null;
  });

  setProfiles(profiles: IntegrationProfileResponse[]): void {
    this.profilesSignal.set(profiles);

    if (profiles.length === 0) {
      this.persistSelectedProfile(null);
      return;
    }

    const currentId = this.selectedProfileIdSignal();
    const currentStillValid =
      currentId !== null && profiles.some((p) => p.integrationProfileId === currentId);

    if (currentStillValid) {
      return;
    }

    const first = profiles[0];
    if (first != null) {
      this.persistSelectedProfile(first.integrationProfileId);
    }
  }

  setSelectedProfile(integrationProfileId: string | null): void {
    this.persistSelectedProfile(integrationProfileId);
  }

  setProfilesLoading(loading: boolean): void {
    this.profilesLoadingSignal.set(loading);
  }

  private persistSelectedProfile(integrationProfileId: string | null): void {
    this.selectedProfileIdSignal.set(integrationProfileId);

    try {
      if (integrationProfileId === null || integrationProfileId.trim().length === 0) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      localStorage.setItem(STORAGE_KEY, integrationProfileId);
    } catch (error) {
      console.error('Failed to persist selected integration profile:', error);
    }
  }

  private readStoredProfileId(): string | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === null || stored.trim().length === 0) {
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }
}
